/**
 * MetricsChartsWidget — Time-series charts for ABET metrics
 * Shows: Ingest rate, API credits, response times over selectable time ranges
 * Data source: abet-metrics-collector CF Worker /metrics endpoint → Supabase abet_metrics
 * Refresh: 60s
 * CSS prefix: mcw-
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { fetchMetricsHistory, fetchMetricsStatus } from '../lib/api'
import { BarChart2 } from 'lucide-react'
import './MetricsChartsWidget.css'

const TIME_RANGES = [
  { label: '1h', hours: 1 },
  { label: '6h', hours: 6 },
  { label: '24h', hours: 24 },
  { label: '7d', hours: 168 },
]

const CHART_CONFIGS = [
  { domain: 'odds_engine', metric: 'ingest_rate_per_min', title: 'Ingest Rate (rec/min)', color: '#22c55e', format: v => v?.toFixed(1) },
  { domain: 'odds_engine', metric: 'active_book_count', title: 'Active Bookmakers', color: '#3b82f6', format: v => Math.round(v) },
  { domain: 'theoddsapi', metric: 'api_credits_pct', title: 'API Credit Usage (%)', color: '#eab308', format: v => `${v?.toFixed(0)}%` },
  { domain: 'mlb_monitors', metric: 'monitors_alive', title: 'Monitors Alive (/5)', color: '#8b5cf6', format: v => Math.round(v) },
]

function MiniChart({ data, color, height = 160 }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || !data.length) return

    const ctx = canvas.getContext('2d')
    const w = canvas.width = canvas.offsetWidth * 2
    const h = canvas.height = height * 2
    ctx.scale(2, 2)
    const rw = w / 2, rh = h / 2

    const values = data.map(d => d.value).filter(v => v != null)
    if (!values.length) return

    const min = Math.min(...values)
    const max = Math.max(...values)
    const range = max - min || 1
    const padY = 10

    ctx.clearRect(0, 0, rw, rh)

    // Grid lines
    ctx.strokeStyle = '#2d374833'
    ctx.lineWidth = 0.5
    for (let i = 0; i <= 4; i++) {
      const y = padY + ((rh - padY * 2) * i / 4)
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(rw, y); ctx.stroke()
    }

    // Data line
    ctx.strokeStyle = color
    ctx.lineWidth = 1.5
    ctx.beginPath()
    data.forEach((d, i) => {
      if (d.value == null) return
      const x = (i / (data.length - 1)) * rw
      const y = padY + ((1 - (d.value - min) / range) * (rh - padY * 2))
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    })
    ctx.stroke()

    // Fill under
    ctx.lineTo(rw, rh)
    ctx.lineTo(0, rh)
    ctx.closePath()
    ctx.fillStyle = color + '15'
    ctx.fill()
  }, [data, color, height])

  if (!data.length) return <div className="mcw-no-data">No data available</div>

  return <canvas ref={canvasRef} className="mcw-sparkline" style={{ height }} />
}

export default function MetricsChartsWidget() {
  const [hours, setHours] = useState(24)
  const [chartData, setChartData] = useState({})
  const [currentValues, setCurrentValues] = useState({})
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)

    // Get current snapshot
    const statusRes = await fetchMetricsStatus()
    if (statusRes.status === 'ok' && statusRes.data?.snapshot) {
      const snap = statusRes.data.snapshot
      const cv = {}
      for (const cfg of CHART_CONFIGS) {
        const domainData = snap[cfg.domain]
        if (domainData?.[cfg.metric]) {
          cv[`${cfg.domain}.${cfg.metric}`] = domainData[cfg.metric].value
        }
      }
      setCurrentValues(cv)
    }

    // Get historical data for each chart
    const results = {}
    await Promise.all(CHART_CONFIGS.map(async (cfg) => {
      const key = `${cfg.domain}.${cfg.metric}`
      const res = await fetchMetricsHistory(cfg.domain, cfg.metric, hours)
      if (res.status === 'ok' && res.data.length) {
        // Sort chronologically and deduplicate
        results[key] = res.data
          .sort((a, b) => new Date(a.ts) - new Date(b.ts))
          .map(d => ({ ts: d.ts, value: d.value }))
      } else {
        results[key] = []
      }
    }))

    setChartData(results)
    setLoading(false)
  }, [hours])

  useEffect(() => {
    fetchData()
    const id = setInterval(fetchData, 60000)
    return () => clearInterval(id)
  }, [fetchData])

  return (
    <div className="mcw-container">
      <div className="mcw-header">
        <h3><BarChart2 size={16} /> Metrics History</h3>
        <div className="mcw-time-select">
          {TIME_RANGES.map(r => (
            <button
              key={r.hours}
              className={`mcw-time-btn ${hours === r.hours ? 'active' : ''}`}
              onClick={() => setHours(r.hours)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mcw-charts">
        {CHART_CONFIGS.map(cfg => {
          const key = `${cfg.domain}.${cfg.metric}`
          const data = chartData[key] || []
          const current = currentValues[key]

          return (
            <div key={key} className="mcw-chart-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div className="mcw-chart-title">{cfg.title}</div>
                {current != null && (
                  <div className="mcw-current-val" style={{ color: cfg.color }}>
                    {cfg.format(current)}
                  </div>
                )}
              </div>
              <div className="mcw-chart-area">
                {loading && !data.length ? (
                  <div className="mcw-no-data">Loading...</div>
                ) : (
                  <MiniChart data={data} color={cfg.color} />
                )}
              </div>
              {data.length > 0 && (
                <div className="mcw-chart-footer">
                  <span>{new Date(data[0]?.ts).toLocaleTimeString()}</span>
                  <span>{data.length} data points</span>
                  <span>{new Date(data[data.length - 1]?.ts).toLocaleTimeString()}</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
